// A message a person reads is a plain sentence. Text that still holds an identifier,
// a path, a code or exception text is never shown; the caller's fixed sentence is.
//
// The rule rejects identifier-shaped TOKENS and known machine PHRASES, never single
// characters. A "<", ">", "=" or "_" used as a word or a symbol is fine ("Premium must
// be >= $1,000", "Company A_B Inc already exists"). Trade-offs, on purpose:
// - a lower-case word joined to digits by a hyphen ("db-01") or any hyphen-joined token
//   of three or more parts ("uwb-api-prod") is an identifier; phrases such as
//   "up-to-date" and "mother-in-law" (a small word in the middle) stay;
// - a host name ("acme.com") is rejected; an e-mail address or a file name with a
//   common extension is something a person typed or uploaded and is kept;
// - a name with one inner capital after a short prefix ("eHealth", "iPhone") is kept,
//   a camelCase identifier ("expirationDate") is not.

const EDGE_PUNCTUATION = /^[.,;:!?()"'`]+|[.,;:!?()"'`]+$/g;

const PHRASES = [
  /internal server error/i,
  /bad gateway|core dumped|null pointer|broken pipe|out of memory|deadlock|syntax error/i,
  /connection closed/i,
  /duplicate (?:key|entry)/i,
  /\bUPDATE\b[\s\S]+\bSET\b/,
  /\b(?:invalid|expired|missing|bad) (?:token|api ?key|secret)\b|\bauthorization (?:failed|header)\b|\bbearer\b/i,
  /network error/i,
  /failed to fetch/i,
  /segmentation fault/i,
  /connection (?:refused|reset|timed out)/i,
  /timeout of \d+ ?ms exceeded/i,
  /request failed with status code/i,
  /cannot read propert(?:y|ies) of/i,
  /duplicate key value/i,
  /violates (?:unique|foreign key|not-null|check) constraint/i,
  /\bunexpected token\b/i,
  /\bis not (?:defined|a function)\b/i,
  /\bundefined\b/,
  /traceback|\berrno\b|max retries|httpconnection|\bat 0x/i,
  /\berror\s*[:#]?\s*[1-5]\d\d\b/i, // "Error 500"
  /\b(?:http|status|code)\s*[:=]?\s*[1-5]\d\d\b/i,
  /\bE(?:CONN\w*|TIMEDOUT|NOTFOUND|HOSTUNREACH|PIPE|AI_\w+)\b/, // ECONNREFUSED, ETIMEDOUT
  /\bbearer\s+\S+/i,
  /\b(?:api[-_ ]?key|secret|passw(?:or)?d|token)\b\s*[:=]?\s*(?=\S*\d)\S{8,}/i,
  /\bSELECT\b[\s\S]+\bFROM\b|\bINSERT\s+INTO\b|\bDELETE\s+FROM\b|\bDROP\s+TABLE\b|\bALTER\s+TABLE\b/,
  /\bselect\s+\*\s+from\b/i,
  /<\/?[A-Za-z][^>]*>|<!--/, // an HTML tag or comment, not a bare "<"
  /:\/\/|\blocalhost\b|\b\d{1,3}(?:\.\d{1,3}){3}\b/i,
  /\b[0-9a-f]{16,}\b/i, // a hex id
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i,
  /\b[A-Za-z]:\\/, // C:\...
  /(?:^|\s)\/(?:[\w.-]+\/)+[\w.-]*/, // /var/log/app
  /(?:^|\s)\/(?:var|srv|usr|etc|opt|home|tmp|app|bin|mnt)\b/,
  /\b(?:[a-z0-9-]+\.)+(?:com|net|org|io|dev|co|us|uk|ca|ai|app|cloud|edu|gov|info|biz|xyz|internal|local|lan|corp|test|example)\b/i,
];

// What a person typed or uploaded is theirs to read back: e-mail addresses and file names.
const PERSONAL_TEXT =
  /[^\s@]+@[^\s@]+\.[A-Za-z]{2,}|\b[\w-]+(?:\.[\w-]+)*\.(?:pdf|docx?|xlsx?|csv|png|jpe?g|txt|zip)\b/gi;

const SMALL_WORDS = new Set(["in", "to", "of", "the", "and", "or", "for", "at", "on", "by", "a", "an"]);
const DATE_PLACEHOLDER = /^(?:YYYY|MM|DD)(?:-(?:YYYY|MM|DD))+$/;

const isIdentifierToken = (raw) => {
  const token = raw.replace(EDGE_PUNCTUATION, "");
  if (!token) return false;
  if (/[[\]{}]/.test(token)) return true; // paths and JSON
  if (/^[A-Za-z_][\w.-]*=\S*$/.test(token)) return true; // key=value
  if (token.includes("\\")) return true;
  if (token.includes("_")) {
    const segments = token.split("_");
    if (segments.length > 1 && segments.some((segment) => /[A-Za-z0-9]{2,}/.test(segment))) return true;
  }
  if (/^[a-z]{3,}[A-Z]\w*$/.test(token)) return true; // camelCase, not eHealth
  if (/^[A-Za-z]+(?:Error|Exception)$/.test(token)) return true; // a class name
  const dotted = token.split(".");
  if (dotted.length > 1 && /^[a-z]/.test(token) && dotted.every((part) => /^[A-Za-z][A-Za-z0-9]{2,}$/.test(part))) {
    return true; // insured.state
  }
  if (token.includes("-") && !DATE_PLACEHOLDER.test(token)) {
    const parts = token.split("-");
    const plain = parts.every((part) => /^[A-Za-z0-9]+$/.test(part));
    const digitsOnly = parts.every((part) => /^\d+$/.test(part));
    if (plain && !digitsOnly) {
      // A year is a word: "pre-2020", "FY-2025" and "renewal-2026" are not identifiers.
      const isYear = (part) => /^(?:19|20)\d{2}$/.test(part);
      if (/^[a-z]{2,}-\d+$/.test(token) && !isYear(parts[1])) return true; // db-01
      if (parts.some((part) => /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]{4,}$/.test(part))) return true; // 7d9f8b6c5, xk2lp
      // P-1234567, but not a year.
      if (
        parts.some((part) => /^\d{4,}$/.test(part) && !isYear(part)) &&
        parts.some((part) => /[A-Za-z]/.test(part))
      ) {
        return true;
      }
      if (parts.length >= 3 && !parts.some((part) => SMALL_WORDS.has(part.toLowerCase()))) return true;
    }
  }
  return false;
};

export const hasMachineText = (text) => {
  const value = String(text ?? "").replace(PERSONAL_TEXT, " ");
  if (PHRASES.some((phrase) => phrase.test(value))) return true;
  return value.split(/\s+/).some((token) => isIdentifierToken(token));
};

/** True when ``text`` is a plain sentence a person can be shown. */
export const isReadable = (text) => {
  const value = String(text ?? "").trim();
  return value !== "" && !hasMachineText(value);
};

/** ``text`` trimmed when it is a plain sentence, else ``fallback``. */
export const readableReason = (text, fallback = "") => {
  const value = String(text ?? "").trim();
  return isReadable(value) ? value : fallback;
};
